import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { CostEstimate, ErrorAlert, GenerateEstimateInput } from '@xdocs/schemas';
import { useDebouncedValue } from '@xdocs/ui';
import { useWs } from '../../ws/WsProvider.js';

export interface UseGenerateEstimateResult {
  estimate: CostEstimate | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
}

/**
 * F48 — live cost chip. Debounces the wizard's current options (~400ms,
 * slightly above the datatable-search 250ms baseline since this call is
 * heavier and fires on every keystroke across several fields at once) then
 * calls `generate.estimate`, the SAME estimator that later gates `generate.
 * start`'s debit — quoted = charged (§21.2).
 */
export function useGenerateEstimate(input: GenerateEstimateInput): UseGenerateEstimateResult {
  const { wsClient, connectionState } = useWs();
  const debounced = useDebouncedValue(input, 400);
  const enabled = connectionState === 'connected';

  const query = useQuery<CostEstimate, ErrorAlert>({
    queryKey: ['generate', 'estimate', debounced],
    queryFn: () => requestTyped(wsClient, 'generate.estimate', debounced),
    enabled,
  });

  return {
    estimate: query.data,
    isLoading: query.isLoading || query.isFetching,
    isError: query.isError,
    error: query.isError ? query.error : null,
  };
}
